import React, { useState } from "react";
import { useLocation, useNavigate } from "react-router-dom";
import styled from "styled-components";
import SongDTO from "../common/dto/SongDTO";
import SimpleButton from "../components/Buttons/SimpleButton";
import Column from "../components/Column/Column";
import HalfColumn from "../components/Column/HalfColumn";
import ErrorInputWithIcon from "../components/Inputs/ErrorInputWithIcon";
import InputWithIcon from "../components/Inputs/InputWithIcon";
import InputWithLabel from "../components/InputWithLabel/InputWithLabel";
import { NavBar } from "../components/NavBar";
import { QuantityPicker } from "../components/QuantityPicker/QuantityPicker";
import RadioOptions from "../components/Radio/RadioOptions";
import FullHeightRow from "../components/Row/FullHeightRow";
import InputErrorText from "../components/Text/InputErrorText";
import RequiredText from "../components/Text/RequiredText";
import Text from "../components/Text/Text";
import ErrorTextAreaIcon from "../components/TextArea/ErrorTextAreaWithIcon";
import { Timeline } from "../components/Timeline/Timeline";
import { useAuth } from "../hooks/useAuth";
import eth_icon from "../imgs/eth_icon_blue.svg";
import music_note from "./imgs/music_note.svg";
import paperclip from "./imgs/paperclip.svg";
import song_desc_icon from "./imgs/song_desc_icon.svg";
import upload_icon from "./imgs/upload.svg";

interface UploadSongDetailsProps {
  theme: string;
  toggleTheme: () => void;
}

const UploadSongDetailsBackground = styled.div`
  background: #000d30;
  background-size: cover;
  width: 100vw;
  min-height: 100vh;
  position: relative;
`;

const UploadSongDetailsContent = styled.div`
  display: flex;
  padding-top: 10%;
  position: relative;
  justify-content: center;
  align-items: center;
`;

const UploadSongButton = styled.div`
  display: flex;
  justify-content: center;
  align-items: center;
  background: transparent;
  color: #fff;
  z-index: 1;
  white-space: nowrap;
  padding-inline: 3%;
  padding-top: 1%;
  padding-bottom: 1%;
  text-align: center;
  margin-bottom: 2%;
`;

const SongInputContainer = styled.div`
  display: flex;
  flex-direction: column;
  width: 80%;
  gap: 24px;
`;

const RequiredEthText = styled(RequiredText)`
  &:after {
    content: "* (ETH)";
  }
`;

const SongCoverImage = styled("img")<{ valid: boolean }>`
  position: absolute;

  top: 0;
  width: 100%;
  height: 100%;
  border-radius: 40px;
  object-fit: cover;
  border: ${(props) => (!props.valid ? "2px solid #FB4EFF" : "")};
`;

const NavButton = styled(SimpleButton)`
  margin-top: 5%;
  max-width: 30%;
`;

interface UploadSongDetailsPageState {
  songFile: File;
  timelineEvents: Array<string>;
  masterCopy: boolean;
}

const SongUploadColumn = styled(HalfColumn)`
  justify-content: center;
  align-items: center;
  border-radius: 40px;
`;

const UploadSongCoverOverlay = styled.label`
  position: relative;
  background: rgba(196, 196, 196, 0.1);
  border: 0.5px solid #ffffff;
  border-radius: 40px;
  width: 30vw;
  height: 50vh;
  display: flex;
  justify-content: center;
  align-items: center;

  &:active {
    transform: scale(0.98);
    box-shadow: 3px 2px 22px 1px rgba(0, 0, 0, 0.24);
  }
  &:hover {
    cursor: pointer;
  }
`;

const SongUploadContainer = styled(FullHeightRow)`
  width: 100%;
  margin-bottom: 5%;
  margin-inline: 20%;

  margin-top: 3%;
  @media screen and (max-height: 1080px) {
    margin-top: 7%;
  }
`;

export const UploadSongDetailsPage: React.FC<UploadSongDetailsProps> = ({
  theme,
  toggleTheme,
}) => {
  const [ethPrice, setEthPrice] = useState("");
  const [description, setDescription] = useState("");
  const [songDisplayOption, setSongDisplayOption] = useState("");
  const [songDisplayOptionValid, setSongDisplayOptionValid] = useState(true);
  const [songName, setSongName] = useState("");
  const [spotifyURL, setSpotifyURL] = useState("");
  const [youtubeURL, setYoutubeURL] = useState("");
  const [validInputEth, setValidInputEth] = useState(true);
  const [songCoverImage, setSongCoverImage] = useState("");
  const [selectedSongCoverFile, setSelectedSongCoverFile] = useState<File>();
  const [songNameValid, setSongNameValid] = useState(true);
  const [descriptionValid, setDescriptionValid] = useState(true);
  const [validSongCover, setValidSongCover] = useState(true);
  const [quantity, setQuantity] = useState(10);
  const navigate = useNavigate();
  const auth = useAuth();
  const location = useLocation();
  const state = location.state as UploadSongDetailsPageState;
  const decimalNumberRegex = /^\d*\.?\d+$/;

  const onEthPriceChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    const num = event.target.value;
    if (!decimalNumberRegex.test(num)) {
      setValidInputEth(false);
      setEthPrice(event.target.value);
      return;
    }

    setValidInputEth(true);
    setEthPrice(event.target.value);
  };
  const onSongNameChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    setSongName(event.target.value);
    setSongNameValid(event.target.value !== "");
  };

  const onSpotifyURLChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    setSpotifyURL(event.target.value);
  };

  const onYoutubeURLChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    setYoutubeURL(event.target.value);
  };

  const onDescriptionChange = (
    event: React.ChangeEvent<HTMLTextAreaElement>
  ) => {
    setDescription(event.target.value);
    setDescriptionValid(event.target.value !== "");
  };

  const validInputs = () => {
    return songName !== "" &&
      description != "" &&
      validInputEth &&
      ethPrice !== "" &&
      selectedSongCoverFile !== undefined &&
      songDisplayOption !== "";
  };

  const onNextClick = () => {
    if (validInputs()) {
      if (auth && auth.user && selectedSongCoverFile) {
        const song = new SongDTO(
          auth.user.wallet_address,
          selectedSongCoverFile,
          songName,
          false,
          description,
          ethPrice,
          state.songFile,
          state.masterCopy ? "1" : quantity.toString(),
          state.masterCopy ? "0" : "1",
          null,
          songDisplayOption === "Full Length",
          spotifyURL,
          youtubeURL
        );

        if (state.masterCopy) {
          navigate("/ecr_terms_conditions", {
            state: {
              songDto: song,
              timelineEvents: state.timelineEvents,
              masterCopy: true,
            },
          });
        } else {
          navigate("/list_options", {
            state: {
              songDto: song,
              timelineEvents: state.timelineEvents,
              masterCopy: false,
              quantity: quantity,
            },
          });
        }
      }
    } else {
      setSongNameValid(songName !== "");
      let isValidPrice: boolean = decimalNumberRegex.test(ethPrice);
      setValidInputEth(isValidPrice);
      setDescriptionValid(description !== "");
      setValidSongCover(selectedSongCoverFile !== undefined);
      setSongDisplayOptionValid(songDisplayOption !== "");
    }
  };

  const onSongCoverChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    if (event.target.files && event.target.files.length > 0) {
      setSongCoverImage(URL.createObjectURL(event.target.files[0]));
      setSelectedSongCoverFile(event.target.files[0]);
      setValidSongCover(true);
    }
  };

  const onQuantityChange = (newQuantity: number) => {
    setQuantity(newQuantity);
  };

  return (
    <>
      <NavBar theme={theme} themeToggler={toggleTheme} hideNavLinks={true} />
      <UploadSongDetailsBackground>
        <UploadSongDetailsContent>
          <Timeline
            events={state.timelineEvents}
            currentEventIndex={1}
            header={
              state.masterCopy
                ? "Creating Master Copy NFT"
                : "Creating Collectible NFT"
            }
          />
          <SongUploadContainer>
            <Column style={{ marginBottom: "auto" }}>
              <UploadSongCoverOverlay>
                <UploadSongButton>
                  <img
                    src={upload_icon}
                    style={{ marginBottom: "10px", marginRight: "10px" }}
                  />
                  Upload Cover Image
                </UploadSongButton>

                {songCoverImage !== "" && (
                  <SongCoverImage src={songCoverImage} valid={validSongCover} />
                )}
                <input
                  style={{ display: "none", width: "30vw", height: "30vh" }}
                  accept="image/*"
                  id={"profilePic"}
                  type="file"
                  onChange={onSongCoverChange}
                />
              </UploadSongCoverOverlay>

              {!validSongCover && (
                <InputErrorText>Song cover must be uploaded!</InputErrorText>
              )}
            </Column>
            <SongUploadColumn>
              <SongInputContainer>
                <InputWithLabel>
                  <RequiredText>Song Name</RequiredText>
                  <ErrorInputWithIcon
                    valid={songNameValid}
                    src={music_note}
                    onChange={onSongNameChange}
                    placeholder="enter song name here"
                  />
                </InputWithLabel>
                {!songNameValid && (
                  <InputErrorText>Field must not be empty</InputErrorText>
                )}

                <InputWithLabel>
                  <Text>Spotify Song URL</Text>
                  <InputWithIcon
                    src={paperclip}
                    onChange={onSpotifyURLChange}
                    placeholder="enter song link here"
                  />
                </InputWithLabel>

                <InputWithLabel>
                  <Text>Youtube Song URL</Text>
                  <InputWithIcon
                    src={paperclip}
                    onChange={onYoutubeURLChange}
                    placeholder="enter song link here"
                  />
                </InputWithLabel>
                <Column>
                  <RequiredText>Song Display Option</RequiredText>

                  <RadioOptions
                    selected={songDisplayOption}
                    onChanged={(event) => {
                      setSongDisplayOption(event.target.name);
                    }}
                    radioOptions={["30s Preview", "Full Length"]}
                    error={!songDisplayOptionValid}
                  />

                  {!songDisplayOptionValid && (
                    <InputErrorText>Field must be selected</InputErrorText>
                  )}
                </Column>
                <InputWithLabel>
                  <RequiredText>Song Description</RequiredText>
                </InputWithLabel>

                <ErrorTextAreaIcon
                  valid={descriptionValid}
                  rows={10}
                  cols={10}
                  src={song_desc_icon}
                  onChange={onDescriptionChange}
                  placeholder="enter song description here"
                />
                {!descriptionValid && (
                  <InputErrorText>Field must not be empty</InputErrorText>
                )}

                <InputWithLabel>
                  <RequiredEthText>Price</RequiredEthText>
                  <ErrorInputWithIcon
                    valid={validInputEth}
                    src={eth_icon}
                    onChange={onEthPriceChange}
                    placeholder="enter list price here"
                  />
                </InputWithLabel>
                {!validInputEth && (
                  <InputErrorText>Input must be number!</InputErrorText>
                )}

                {!state.masterCopy && (
                  <InputWithLabel style={{ width: "50%", marginBottom: "8%" }}>
                    <RequiredText>Max Quantity</RequiredText>
                    <QuantityPicker
                      value={quantity}
                      onChange={onQuantityChange}
                      max={10000}
                    />
                  </InputWithLabel>
                )}

                <NavButton onClick={onNextClick}>Next</NavButton>
              </SongInputContainer>
            </SongUploadColumn>
          </SongUploadContainer>
        </UploadSongDetailsContent>
      </UploadSongDetailsBackground>
    </>
  );
};
